"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"

interface CollectionStatsResponse {
  collections: Array<{
    _id: string
    name: string
    contractAddress: string
    blockchain: string
    collectionImage?: string
    status: string
    raribleStats?: {
      items?: number
      owners?: number
      listedItems?: number
      dayVolume?: {
        amount?: string
        currency?: {
          usdExchangeRate?: string
        }
      }
      totalVolume?: {
        amount?: string
        currency?: {
          usdExchangeRate?: string
        }
      }
    } | null
  }>
  totalCollections: number
  totalCollectionsWithStats?: number
  exists: boolean
}
import { Toolbar } from "@/app/layout/toolbar/toolbar"
import { preloadFont, validateBrandFont } from "@/app/utils/font-validation"
import { useEffect } from "react"
import { BrandThemeProvider } from "./brand-theme-provider"
import { SectionRenderer } from "./sections/section-renderer"

interface MerchantData {
  merchant?: {
    _id: string
    name: string
    slug: string
    description?: string
    logo?: string
    website?: string
    twitter?: string
    discord?: string
    instagram?: string
    telegram?: string
    youtube?: string
    environmentId: string
    isActive: boolean
    createdAt: string
    updatedAt: string
    preferredBlockchain?: string
    supportedBlockchains?: string[]
    branding?: {
      colors: {
        bgColor: string
        textColor: string
        primaryColor: string
        secondaryColor: string
      }
      font: string
    }
    stats?: {
      totalPages: number
      totalCollections: number
      lastActivity: string
    }
  }
  collections?: Array<{
    _id: string
    contractAddress: string
    blockchain: string
    name: string
    description?: string
    collectionImage?: string
    status: string
    currentVersion: number
    createdAt: string
    updatedAt: string
  }>
  pages: Array<{
    _id: string
    merchantId: string
    slug: string
    title: string
    description?: string
    sections: Array<{
      id: string
      type:
        | "info"
        | "list"
        | "slider"
        | "collections"
        | "collectors"
        | "banner"
        | "image"
        | "modal"
      order: number
      isVisible: boolean
      props: Record<string, unknown>
      styles?: {
        backgroundColor?: string
        textColor?: string
        padding?: string
        margin?: string
      }
    }>
    visibility?: string
    theme?: Record<string, unknown>
    environmentId?: string
    isActive?: boolean
    createdAt?: string
    updatedAt?: string
  }>
  exists: boolean
}

interface Page {
  _id: string
  merchantId: string
  slug: string
  title: string
  description?: string
  sections: Array<{
    id: string
    type:
      | "info"
      | "list"
      | "slider"
      | "collections"
      | "collectors"
      | "banner"
      | "image"
      | "video"
      | "modal"
    order: number
    isVisible: boolean
    props: Record<string, unknown>
    styles?: {
      backgroundColor?: string
      textColor?: string
      padding?: string
      margin?: string
    }
  }>
  visibility?: string
  theme?: {
    primaryColor: string
    secondaryColor: string
    backgroundColor: string
    textColor: string
    fontFamily: string
  }
  environmentId?: string
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
}

interface MerchantClientProps {
  merchantData: MerchantData
  page?: Page
}

// Custom hook for fetching collections statistics
function useCollectionsStatistics(merchantSlug?: string) {
  const bffApiClient = useMolecule(BffApiClientMolecule)

  return useQuery({
    queryKey: ["collections-statistics", merchantSlug],
    queryFn: async () => {
      if (!merchantSlug) throw new Error("Merchant slug is required")

      const response = await bffApiClient.api.merchants.slug[
        ":slug"
      ].collections.statistics.$get({
        param: { slug: merchantSlug },
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch collections statistics: ${response.status}`,
        )
      }

      const data = (await response.json()) as CollectionStatsResponse
      return data
    },
    enabled: !!merchantSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })
}

export const BrandClient = reatomComponent(
  ({ merchantData, page }: MerchantClientProps) => {
    const logger = useMolecule(LoggerMolecule)

    const {
      data: collectionsStats,
      isLoading: statsLoading,
      error: statsError,
    } = useCollectionsStatistics(merchantData.merchant?.slug)

    const brandPageContext: EventContext = {
      merchantId: merchantData.merchant?._id || "unknown",
      merchantName: merchantData.merchant?.name || "unknown",
      merchantSlug: merchantData.merchant?.slug || "unknown",
      pageId: page?._id || "homepage",
      pageSlug: page?.slug || "homepage",
      pageTitle: page?.title || merchantData.merchant?.name || "unknown",
      sectionsCount: page?.sections?.length?.toString() || "0",
      collectionsCount: merchantData.collections?.length?.toString() || "0",
      flow: Flow.BrandPage,
      isHomepage: (!page || page.slug === "homepage").toString(),
      preferredBlockchain:
        merchantData.merchant?.preferredBlockchain || "ethereum",
    }

    logger?.api.pushEvent("Brand page viewed", brandPageContext)
    amplitude.track("Brand page viewed", brandPageContext)

    const brandTheme =
      page?.theme ||
      (merchantData.merchant?.branding
        ? {
            backgroundColor: merchantData.merchant.branding.colors.bgColor,
            textColor: merchantData.merchant.branding.colors.textColor,
            fontFamily: merchantData.merchant.branding.font,
          }
        : undefined)

    useEffect(() => {
      const fontToPreload =
        page?.theme?.fontFamily || merchantData.merchant?.branding?.font

      if (fontToPreload) {
        const fontValidation = validateBrandFont(fontToPreload)

        if (fontValidation.isValid && fontValidation.needsLoading) {
          preloadFont(fontValidation.sanitizedName)
        }
      }
    }, [page?.theme?.fontFamily, merchantData.merchant?.branding?.font])

    // Calculate aggregated statistics from Rarible data
    const calculateAggregatedStats = () => {
      if (!collectionsStats?.collections || statsLoading) {
        const fallbackStats = {
          totalCollections: merchantData.merchant?.stats?.totalCollections || 0,
          totalItems: 0,
          volume24h: "$0",
          percentListed: "0%",
        }
        return fallbackStats
      }

      const collections = collectionsStats.collections
      let totalItems = 0
      let totalVolumeUSD = 0
      let totalListed = 0
      let collectionsWithStats = 0
      let totalOwners = 0

      for (const collection of collections) {
        const stats = collection.raribleStats

        if (stats) {
          collectionsWithStats++
          totalItems += stats.items || 0
          totalListed += stats.listedItems || 0
          totalOwners += stats.owners || 0

          // Sum up total volume (all-time) with proper USD conversion
          if (
            stats.totalVolume?.amount &&
            stats.totalVolume?.currency?.usdExchangeRate
          ) {
            const volumeETH = Number.parseFloat(stats.totalVolume.amount) || 0
            const usdRate =
              Number.parseFloat(stats.totalVolume.currency.usdExchangeRate) || 0
            const volumeUSD = volumeETH * usdRate
            totalVolumeUSD += volumeUSD
          }
        }
      }

      const percentListed =
        totalItems > 0 ? ((totalListed / totalItems) * 100).toFixed(1) : "0"

      const finalStats = {
        totalCollections: collections.length,
        totalItems: totalOwners, // Total holders for "Unique Collectors" display
        totalVolume:
          totalVolumeUSD > 0
            ? `$${Math.round(totalVolumeUSD).toLocaleString()}`
            : "$0", // Total all-time volume across all collections
        percentListed: `${percentListed}%`, // % of items currently listed
        // Additional detailed stats for reference
        totalOwners,
        totalNFTs: totalItems,
        totalVolumeUSD:
          totalVolumeUSD > 0
            ? `$${Math.round(totalVolumeUSD).toLocaleString()}`
            : "$0",
      }

      return finalStats
    }

    return (
      <BrandThemeProvider
        merchantBranding={merchantData.merchant?.branding}
        pageTheme={page?.theme}
      >
        <div className="flex flex-1 flex-col">
          {/* Page Content */}
          {page?.sections && page.sections.length > 0 ? (
            <>
              {page.sections
                .filter((section) => section.isVisible !== false)
                .sort((a, b) => a.order - b.order)
                .map((section, index) => {
                  // Full-width sections (banner, video, image) render without padding
                  const isFullWidth =
                    section.type === "banner" ||
                    section.type === "video" ||
                    section.type === "image"

                  // Check if this is the first section and if it's NOT full-width
                  const isFirstSection = index === 0
                  const needsTopSpacing = isFirstSection && !isFullWidth

                  const sectionElement = (
                    <SectionRenderer
                      key={section.id}
                      section={section}
                      merchantBlockchain={
                        merchantData.merchant?.preferredBlockchain || "ethereum"
                      }
                      merchantData={{
                        name: merchantData.merchant?.name,
                        description: merchantData.merchant?.description,
                        logo: merchantData.merchant?.logo,
                        website: merchantData.merchant?.website,
                        twitter: merchantData.merchant?.twitter?.startsWith(
                          "http",
                        )
                          ? merchantData.merchant.twitter
                          : merchantData.merchant?.twitter
                            ? `https://twitter.com/${merchantData.merchant.twitter.replace("@", "")}`
                            : undefined,
                        discord: merchantData.merchant?.discord,
                        instagram: merchantData.merchant?.instagram?.startsWith(
                          "http",
                        )
                          ? merchantData.merchant.instagram
                          : merchantData.merchant?.instagram
                            ? `https://instagram.com/${merchantData.merchant.instagram.replace("@", "")}`
                            : undefined,
                        youtube: merchantData.merchant?.youtube?.startsWith(
                          "http",
                        )
                          ? merchantData.merchant.youtube
                          : merchantData.merchant?.youtube
                            ? `https://youtube.com/${merchantData.merchant.youtube.startsWith("@") ? merchantData.merchant.youtube : `@${merchantData.merchant.youtube}`}`
                            : undefined,
                      }}
                      collectionStats={calculateAggregatedStats()}
                      statsLoading={statsLoading}
                      statsError={statsError}
                      brandTheme={{
                        backgroundColor: brandTheme?.backgroundColor,
                        textColor: brandTheme?.textColor,
                      }}
                    />
                  )

                  // Render full-width sections without padding wrapper
                  if (isFullWidth) {
                    return sectionElement
                  }

                  // Render other sections with padding wrapper
                  return (
                    <div
                      key={section.id}
                      className={`mx-auto w-full px-4 py-6 md:px-8 md:py-8 ${
                        needsTopSpacing ? "pt-16 md:pt-20" : ""
                      }`}
                    >
                      {sectionElement}
                    </div>
                  )
                })}
            </>
          ) : (
            <div className="mx-auto w-full px-4 py-6 pt-16 md:px-8 md:py-8 md:pt-20">
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <h2 className="mb-4 font-bold text-2xl">
                    {page?.title ||
                      `Welcome to ${merchantData.merchant?.name || "this merchant"}`}
                  </h2>
                  <p className="text-foreground/70">
                    {page?.title
                      ? "No sections configured for this page."
                      : "This merchant hasn't configured their homepage yet."}
                  </p>
                </div>
              </div>
            </div>
          )}

          <Toolbar
            className="hidden md:block md:px-4"
            brandTheme={brandTheme}
          />
        </div>
      </BrandThemeProvider>
    )
  },
  "BrandClient",
)
